import { useState, useEffect, useMemo, useRef } from 'react';
import { dashboardService, DashboardData } from '../../api/dashboard';
import FilterOptions from './components/FilterOptions';
import { checkinService, CheckinHistory } from '../../api/checkin';
import { fuelingHistoryService, FuelingHistory } from '../../api/fueling.history';
import { historyTripService, HistoryTrip } from '../../api/history.trip';
import { useCallback } from 'react';
import { vehicleService } from '../../api/vehicles';
import {
  Car,
  Users,
  TriangleAlert,
  Fuel,
  Wrench,
  Route,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import {
  PageHeader,
  StatCard,
  SectionCard,
  CardHeader,
  CardBody,
  CardFooter,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Badge,
  IconButton,
} from '../../components/ui/SharedLayout';

interface VeiculoQuilometragem {
  id: string;
  placa: string;
  modelo: string;
  quilometragemAtual: number;
  quilometragemMensal: number;
  mediaConsumo: number;
  ultimaAtualizacao: string;
}

interface Activity {
  id: string;
  data: string;
  veiculo: string;
  tipo: string;
  status: string;
  responsavel: string;
  created_at: string;
}

type ActivityTone = 'green' | 'amber' | 'blue';

const normalizeActivityStatus = (status?: string): { label: string; tone: ActivityTone } => {
  const normalized = (status || '').toLowerCase();

  if (
    normalized.includes('final') ||
    normalized.includes('conclu') ||
    normalized.includes('arquiv')
  ) {
    return {
      label: status || 'Concluído',
      tone: 'green',
    };
  }

  if (
    normalized.includes('pend') ||
    normalized.includes('blocked') ||
    normalized.includes('iniciado')
  ) {
    return {
      label: status || 'Pendente',
      tone: 'amber',
    };
  }

  return {
    label: status || 'Em andamento',
    tone: 'blue',
  };
};

const Dashboard = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [dados, setDados] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [atividades, setAtividades] = useState<Activity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activitiesError, setActivitiesError] = useState<string | null>(null);

  const [quilometragemVeiculos, setQuilometragemVeiculos] = useState<
    VeiculoQuilometragem[]
  >([]);
  const [quilometragemLoading, setQuilometragemLoading] = useState(true);
  const [quilometragemError, setQuilometragemError] = useState<string | null>(null);
  const [quilometragemSearchTerm, setQuilometragemSearchTerm] = useState('');

  const ITEMS_PER_PAGE = 10;
  const [atividadesPage, setAtividadesPage] = useState(1);
  const [quilometragemPage, setQuilometragemPage] = useState(1);

  const statsCarouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const el = statsCarouselRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < maxScroll - 2);
  }, []);

  const handleScrollStats = useCallback((direction: 'left' | 'right') => {
    if (!statsCarouselRef.current) return;
    const container = statsCarouselRef.current;
    const cardWidth = container.clientWidth * 0.92;
    container.scrollBy({
      left: direction === 'left' ? -cardWidth : cardWidth,
      behavior: 'smooth',
    });
    setTimeout(updateScrollButtons, 550);
  }, [updateScrollButtons]);

  useEffect(() => {
    const el = statsCarouselRef.current;
    if (!el) return;
    const run = () => updateScrollButtons();
    run();
    requestAnimationFrame(() => {
      run();
      setTimeout(run, 120);
    });
    const onScroll = () => run();
    const onResize = () => run();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    const t = setTimeout(run, 260);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      clearTimeout(t);
    };
  }, [updateScrollButtons, loading, dados]);

  const filterCategories = {
    'Tipo': [
      { id: 'Abastecimento', label: 'Abastecimento' },
      { id: 'Manutenção', label: 'Manutenção' },
      { id: 'Viagem', label: 'Viagem' }
    ],
    'Status': [
      { id: 'Concluído', label: 'Concluído' },
      { id: 'Pendente', label: 'Pendente' },
      { id: 'Em Andamento', label: 'Em Andamento' }
    ]
  };

  const fetchRecentActivities = useCallback(async () => {
    setActivitiesLoading(true);
    setActivitiesError(null);
    try {
      const results = await Promise.allSettled([
        checkinService.getHistory(),
        fuelingHistoryService.getAllFuelingHistory(),
        historyTripService.getAll(),
      ]);

      const [checkinResult, fuelingResult, tripResult] = results;
      const activities: Activity[] = [];

      const checkinHistory =
        checkinResult.status === 'fulfilled' ? checkinResult.value : [];
      const fuelingHistory =
        fuelingResult.status === 'fulfilled' ? fuelingResult.value : [];
      const tripHistory = tripResult.status === 'fulfilled' ? tripResult.value : [];

      checkinHistory.forEach((item: CheckinHistory) => {
        activities.push({
          id: `checkin-${item.id}`,
          data: item.created_at ? new Date(item.created_at).toLocaleDateString('pt-BR') : '',
          veiculo: item.checkin?.vehicle ? `${item.checkin.vehicle.description} - ${item.checkin.vehicle.license_plate}` : '-',
          tipo: 'Check-in',
          status: item.action || '-',
          responsavel: item.checkin?.employee?.name || '-',
          created_at: item.created_at,
        });
      });

      fuelingHistory.forEach((item: FuelingHistory) => {
        activities.push({
          id: `fueling-${item.id}`,
          data: item.created_at ? new Date(item.created_at).toLocaleDateString('pt-BR') : '',
          veiculo: item.vehicle ? `${item.vehicle.description} - ${item.vehicle.license_plate}` : '-',
          tipo: 'Abastecimento',
          status: item.status || item.action || '-',
          responsavel: item.employee?.name || '-',
          created_at: item.created_at,
        });
      });

      tripHistory.forEach((item: HistoryTrip) => {
        activities.push({
          id: `trip-${item.id}`,
          data: item.created_at ? new Date(item.created_at).toLocaleDateString('pt-BR') : '',
          veiculo: item.vehicle_info || '-',
          tipo: 'Viagem',
          status: item.action || '-',
          responsavel: item.employee_info || '-',
          created_at: item.created_at,
        });
      });

      activities.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setAtividades(activities.slice(0, 20));
      const failedRequests = results.filter((result) => result.status === 'rejected').length;
      if (failedRequests === results.length) {
        setActivitiesError('Erro ao carregar atividades recentes');
      } else if (failedRequests > 0) {
        setActivitiesError('Algumas atividades recentes não puderam ser carregadas.');
      }
    } catch (err) {
      console.error(err);
      setActivitiesError('Erro ao carregar atividades recentes');
    } finally {
      setActivitiesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecentActivities();
  }, [fetchRecentActivities]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    dashboardService.getDashboard()
      .then(data => {
        setDados(data);
        setLoading(false);
      })
      .catch(_err => {
        setError('Erro ao carregar dados do dashboard');
        setLoading(false);
      });
  }, []);

  const fetchVehicleMileage = useCallback(async () => {
    setQuilometragemLoading(true);
    setQuilometragemError(null);
    try {
      const [vehicles, fuelingHistory] = await Promise.all([
        vehicleService.getAllVehicles(),
        fuelingHistoryService.getAllFuelingHistory(),
      ]);
      const now = new Date();
      const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const quilometragemData: VeiculoQuilometragem[] = vehicles.map(vehicle => {
        const fuelings = fuelingHistory.filter(f => f.vehicle_id === vehicle.id && f.odometer_reading != null);
        const fuelings30d = fuelings.filter(f => new Date(f.fueling_time) >= last30Days);
        fuelings.sort((a, b) => new Date(a.fueling_time).getTime() - new Date(b.fueling_time).getTime());
        fuelings30d.sort((a, b) => new Date(a.fueling_time).getTime() - new Date(b.fueling_time).getTime());
        const lastFueling = fuelings[fuelings.length - 1];
        const last30dFueling = fuelings30d[fuelings30d.length - 1];
        const first30dFueling = fuelings30d[0];
        
        const quilometragemAtual = lastFueling?.odometer_reading ?? vehicle.mileage ?? 0;
        
        let quilometragemMensal = 0;
        if (last30dFueling?.odometer_reading && first30dFueling?.odometer_reading) {
          quilometragemMensal = last30dFueling.odometer_reading - first30dFueling.odometer_reading;
        }
        const totalLitros = fuelings30d.reduce((sum, f) => sum + (f.amount || 0), 0);
        const kmDriven = quilometragemMensal;
        const mediaConsumo = totalLitros > 0 ? kmDriven / totalLitros : 0;
        const ultimaAtualizacao = fuelings.length > 0 ? new Date(fuelings[fuelings.length - 1].fueling_time).toLocaleDateString('pt-BR') : (vehicle.updated_at ? new Date(vehicle.updated_at).toLocaleDateString('pt-BR') : '-');
        return {
          id: vehicle.id.toString(),
          placa: vehicle.license_plate,
          modelo: vehicle.description,
          quilometragemAtual: quilometragemAtual || 0,
          quilometragemMensal: quilometragemMensal || 0,
          mediaConsumo: mediaConsumo || 0,
          ultimaAtualizacao,
        };
      });
      setQuilometragemVeiculos(quilometragemData);
      setQuilometragemLoading(false);
    } catch (_err) {
      setQuilometragemError('Erro ao carregar quilometragem dos veículos');
      setQuilometragemLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVehicleMileage();
  }, [fetchVehicleMileage]);

  useEffect(() => {
    if (!quilometragemSearchTerm) {
      fetchVehicleMileage();
      return;
    }

    const term = quilometragemSearchTerm.toLowerCase();
    setQuilometragemVeiculos(prevVeiculos => 
      prevVeiculos.filter((veiculo: VeiculoQuilometragem) => 
        veiculo.placa.toLowerCase().includes(term) ||
        veiculo.modelo.toLowerCase().includes(term)
      )
    );
  }, [quilometragemSearchTerm, fetchVehicleMileage]);

  useEffect(() => {
    setAtividadesPage(1);
  }, [atividades.length]);

  useEffect(() => {
    setQuilometragemPage(1);
  }, [quilometragemVeiculos.length, quilometragemSearchTerm]);

  const atividadesPaginadas = useMemo(() => {
    const start = (atividadesPage - 1) * ITEMS_PER_PAGE;
    return atividades.slice(start, start + ITEMS_PER_PAGE);
  }, [atividades, atividadesPage]);
  const totalAtividadesPages = Math.max(1, Math.ceil(atividades.length / ITEMS_PER_PAGE));

  const quilometragemPaginada = useMemo(() => {
    const start = (quilometragemPage - 1) * ITEMS_PER_PAGE;
    return quilometragemVeiculos.slice(start, start + ITEMS_PER_PAGE);
  }, [quilometragemVeiculos, quilometragemPage]);
  const totalQuilometragemPages = Math.max(1, Math.ceil(quilometragemVeiculos.length / ITEMS_PER_PAGE));

  const handleApplyFilter = (_selectedFilters: Record<string, string[]>) => {
    // filter logic reserved for future use
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        {loading && (
          <div className="py-16 text-center text-sm text-slate-500">Carregando dados do dashboard...</div>
        )}
        {error && (
          <div className="py-16 text-center text-sm text-rose-600">{error}</div>
        )}
        {!loading && !error && dados && (
          <>
            <PageHeader
              title="Dashboard"
              description="Visão geral consolidada da frota, operações e indicadores chave do SuaFrota"
              hideTitleInBody
            />

            <div className="group/carousel relative mt-6">
              {canScrollLeft ? (
                <button
                  type="button"
                  onClick={() => handleScrollStats('left')}
                  aria-label="Ver cards anteriores"
                  className="pointer-events-auto absolute left-[-14px] top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 -translate-x-3 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 opacity-0 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.35)] transition-all duration-200 ease-out hover:border-blue-200 hover:text-blue-700 hover:scale-105 active:scale-95 group-hover/carousel:translate-x-0 group-hover/carousel:opacity-80 focus-visible:translate-x-0 focus-visible:opacity-90 sm:flex"
                >
                  <ChevronLeft className="h-5 w-5 stroke-[2.2]" />
                </button>
              ) : null}

              <div
                ref={statsCarouselRef}
                className="flex w-full snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              >
                <StatCard
                  className="shrink-0 min-h-[180px] w-full snap-start md:w-[calc(50%-8px)] md:min-h-[190px] lg:w-[calc(33.33%-11px)] xl:w-[calc(25%-12px)] xl:min-h-[195px] 2xl:w-[calc(25%-12px)] 2xl:min-h-[200px]"
                  icon={<Car className="h-5 w-5 stroke-[1.8]" />}
                  label="Total de Veículos"
                  value={dados.vehicles}
                  trend={
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-emerald-700">{dados.activeVehicles}</span>
                      <span>ativos</span>
                      <span className="mx-0.5 text-white/60">·</span>
                      <span className="font-semibold text-yellow-700">{dados.vehiclesInMaintenance}</span>
                      <span>em manutenção</span>
                    </span>
                  }
                  variant="deep"
                />

                <StatCard
                  className="shrink-0 min-h-[180px] w-full snap-start md:w-[calc(50%-8px)] md:min-h-[190px] lg:w-[calc(33.33%-11px)] xl:w-[calc(25%-12px)] xl:min-h-[195px] 2xl:w-[calc(25%-12px)] 2xl:min-h-[200px]"
                  icon={<Users className="h-5 w-5 stroke-[1.8]" />}
                  label="Funcionários"
                  value={dados.employees}
                  trend={<span>Colaboradores cadastrados no sistema</span>}
                  variant="primary"
                />

                <StatCard
                  className="shrink-0 min-h-[180px] w-full snap-start md:w-[calc(50%-8px)] md:min-h-[190px] lg:w-[calc(33.33%-11px)] xl:w-[calc(25%-12px)] xl:min-h-[195px] 2xl:w-[calc(25%-12px)] 2xl:min-h-[200px]"
                  icon={<TriangleAlert className="h-5 w-5 stroke-[1.8]" />}
                  label="Incidentes"
                  value={dados.incidents}
                  trend={
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold">{dados.alerts}</span>
                      <span>alertas em aberto</span>
                    </span>
                  }
                  variant="navy"
                />

                <StatCard
                  className="shrink-0 min-h-[180px] w-full snap-start md:w-[calc(50%-8px)] md:min-h-[190px] lg:w-[calc(33.33%-11px)] xl:w-[calc(25%-12px)] xl:min-h-[195px] 2xl:w-[calc(25%-12px)] 2xl:min-h-[200px]"
                  icon={<Fuel className="h-5 w-5 stroke-[1.8]" />}
                  label="Abastecimentos"
                  value={dados.fuelings}
                  trend={
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold">R$ {dados.totalFuelCost?.toLocaleString('pt-BR')}</span>
                      <span>de custo total</span>
                    </span>
                  }
                  variant="sky"
                />

                <StatCard
                  className="shrink-0 min-h-[180px] w-full snap-start md:w-[calc(50%-8px)] md:min-h-[190px] lg:w-[calc(33.33%-11px)] xl:w-[calc(25%-12px)] xl:min-h-[195px] 2xl:w-[calc(25%-12px)] 2xl:min-h-[200px]"
                  icon={<Wrench className="h-5 w-5 stroke-[1.8]" />}
                  label="Manutenção"
                  value={<span>R$ {dados.totalMaintenanceCost?.toLocaleString('pt-BR')}</span>}
                  trend={
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold">{dados.pendingRequests}</span>
                      <span>solicitações pendentes</span>
                    </span>
                  }
                  variant="rich"
                />

                <StatCard
                  className="shrink-0 min-h-[180px] w-full snap-start md:w-[calc(50%-8px)] md:min-h-[190px] lg:w-[calc(33.33%-11px)] xl:w-[calc(25%-12px)] xl:min-h-[195px] 2xl:w-[calc(25%-12px)] 2xl:min-h-[200px]"
                  icon={<Route className="h-5 w-5 stroke-[1.8]" />}
                  label="Viagens"
                  value={dados.trips}
                  trend={
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold">{dados.totalTripDistance || '-'}</span>
                      <span>km rodados no total</span>
                    </span>
                  }
                  variant="teal"
                />
              </div>

              {canScrollRight ? (
                <button
                  type="button"
                  onClick={() => handleScrollStats('right')}
                  aria-label="Ver próximos cards"
                  className="pointer-events-auto absolute right-[-14px] top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 translate-x-3 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 opacity-0 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.35)] transition-all duration-200 ease-out hover:border-blue-200 hover:text-blue-700 hover:scale-105 active:scale-95 group-hover/carousel:translate-x-0 group-hover/carousel:opacity-80 focus-visible:translate-x-0 focus-visible:opacity-90 sm:flex"
                >
                  <ChevronRight className="h-5 w-5 stroke-[2.2]" />
                </button>
              ) : null}
            </div>

            <SectionCard className="mt-8">
              <CardHeader>
                <div>
                  <h2 className="text-sm font-semibold tracking-tight text-slate-900">
                    Atividades Recentes
                  </h2>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Últimas movimentações de check-in, abastecimento e viagens
                  </p>
                </div>
              </CardHeader>
              <CardBody className="!px-0 !py-0">
                {activitiesLoading ? (
                  <div className="py-10 text-center text-sm text-slate-500">Carregando atividades...</div>
                ) : activitiesError ? (
                  <div className="py-10 text-center text-sm text-rose-600">{activitiesError}</div>
                ) : (
                  <ModernTable>
                    <Thead>
                      <Tr className="!hover:bg-transparent">
                        <Th>Data</Th>
                        <Th>Veículo</Th>
                        <Th>Tipo</Th>
                        <Th>Status</Th>
                        <Th>Responsável</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {atividadesPaginadas.length > 0 ? (
                        atividadesPaginadas.map(atividade => {
                          const status = normalizeActivityStatus(atividade.status);
                          return (
                            <Tr key={atividade.id}>
                              <Td className="font-medium text-slate-900">{atividade.data}</Td>
                              <Td>{atividade.veiculo}</Td>
                              <Td>
                                <Badge tone="slate">{atividade.tipo}</Badge>
                              </Td>
                              <Td>
                                <Badge tone={status.tone}>{status.label}</Badge>
                              </Td>
                              <Td>{atividade.responsavel}</Td>
                            </Tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={5} className="py-10 text-center text-sm text-slate-500">
                            Nenhuma atividade encontrada
                          </td>
                        </tr>
                      )}
                    </Tbody>
                  </ModernTable>
                )}
              </CardBody>
              {!activitiesLoading && !activitiesError && atividades.length > 0 && (
                <CardFooter>
                  <div className="text-xs font-medium text-slate-500">
                    Exibindo <span className="font-semibold text-slate-700">{((atividadesPage - 1) * ITEMS_PER_PAGE) + 1}</span>
                    {' – '}
                    <span className="font-semibold text-slate-700">{Math.min(atividadesPage * ITEMS_PER_PAGE, atividades.length)}</span>
                    {' de '}
                    <span className="font-semibold text-slate-700">{atividades.length}</span>
                    {' atividades'}
                  </div>
                  <div className="flex items-center gap-1">
                    <IconButton
                      variant="secondary"
                      disabled={atividadesPage <= 1}
                      onClick={() => setAtividadesPage(1)}
                      aria-label="Primeira página"
                    >
                      <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                    </IconButton>
                    <IconButton
                      variant="secondary"
                      disabled={atividadesPage <= 1}
                      onClick={() => setAtividadesPage(p => Math.max(1, p - 1))}
                      aria-label="Página anterior"
                    >
                      <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                    </IconButton>
                    <div className="px-2.5 text-xs font-semibold text-slate-700 tabular-nums">
                      {atividadesPage} / {totalAtividadesPages}
                    </div>
                    <IconButton
                      variant="secondary"
                      disabled={atividadesPage >= totalAtividadesPages}
                      onClick={() => setAtividadesPage(p => Math.min(totalAtividadesPages, p + 1))}
                      aria-label="Próxima página"
                    >
                      <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                    </IconButton>
                    <IconButton
                      variant="secondary"
                      disabled={atividadesPage >= totalAtividadesPages}
                      onClick={() => setAtividadesPage(totalAtividadesPages)}
                      aria-label="Última página"
                    >
                      <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                    </IconButton>
                  </div>
                </CardFooter>
              )}
            </SectionCard>

            <SectionCard className="mt-8">
              <CardHeader>
                <div>
                  <h2 className="text-sm font-semibold tracking-tight text-slate-900">
                    Quilometragem dos Veículos
                  </h2>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Quilometragem atual, mensal e média de consumo
                  </p>
                </div>
                <div className="relative w-full sm:w-[320px]">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.8] text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Pesquisar por placa ou modelo..."
                    value={quilometragemSearchTerm}
                    onChange={(e) => setQuilometragemSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardBody className="!px-0 !py-0">
                {quilometragemLoading ? (
                  <div className="py-10 text-center text-sm text-slate-500">Carregando quilometragem...</div>
                ) : quilometragemError ? (
                  <div className="py-10 text-center text-sm text-rose-600">{quilometragemError}</div>
                ) : (
                  <ModernTable>
                    <Thead>
                      <Tr className="!hover:bg-transparent">
                        <Th>Placa</Th>
                        <Th>Modelo</Th>
                        <Th>Quilometragem Atual</Th>
                        <Th>Quilometragem Mensal</Th>
                        <Th>Média de Consumo</Th>
                        <Th>Última Atualização</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {quilometragemPaginada.length > 0 ? (
                        quilometragemPaginada.map(veiculo => (
                          <Tr key={veiculo.id}>
                            <Td>
                              <span className="font-semibold tracking-tight text-slate-900">{veiculo.placa}</span>
                            </Td>
                            <Td>{veiculo.modelo}</Td>
                            <Td className="font-semibold tabular-nums text-slate-900">{veiculo.quilometragemAtual.toLocaleString()} km</Td>
                            <Td className="tabular-nums">{veiculo.quilometragemMensal.toLocaleString()} km</Td>
                            <Td className="tabular-nums">{veiculo.mediaConsumo.toFixed(1)} km/L</Td>
                            <Td className="text-slate-500">{veiculo.ultimaAtualizacao}</Td>
                          </Tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="py-10 text-center text-sm text-slate-500">
                            Nenhum veículo encontrado
                          </td>
                        </tr>
                      )}
                    </Tbody>
                  </ModernTable>
                )}
              </CardBody>
              {!quilometragemLoading && !quilometragemError && quilometragemVeiculos.length > 0 && (
                <CardFooter>
                  <div className="text-xs font-medium text-slate-500">
                    Exibindo <span className="font-semibold text-slate-700">{((quilometragemPage - 1) * ITEMS_PER_PAGE) + 1}</span>
                    {' – '}
                    <span className="font-semibold text-slate-700">{Math.min(quilometragemPage * ITEMS_PER_PAGE, quilometragemVeiculos.length)}</span>
                    {' de '}
                    <span className="font-semibold text-slate-700">{quilometragemVeiculos.length}</span>
                    {' veículos'}
                  </div>
                  <div className="flex items-center gap-1">
                    <IconButton
                      variant="secondary"
                      disabled={quilometragemPage <= 1}
                      onClick={() => setQuilometragemPage(1)}
                      aria-label="Primeira página"
                    >
                      <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                    </IconButton>
                    <IconButton
                      variant="secondary"
                      disabled={quilometragemPage <= 1}
                      onClick={() => setQuilometragemPage(p => Math.max(1, p - 1))}
                      aria-label="Página anterior"
                    >
                      <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                    </IconButton>
                    <div className="px-2.5 text-xs font-semibold text-slate-700 tabular-nums">
                      {quilometragemPage} / {totalQuilometragemPages}
                    </div>
                    <IconButton
                      variant="secondary"
                      disabled={quilometragemPage >= totalQuilometragemPages}
                      onClick={() => setQuilometragemPage(p => Math.min(totalQuilometragemPages, p + 1))}
                      aria-label="Próxima página"
                    >
                      <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                    </IconButton>
                    <IconButton
                      variant="secondary"
                      disabled={quilometragemPage >= totalQuilometragemPages}
                      onClick={() => setQuilometragemPage(totalQuilometragemPages)}
                      aria-label="Última página"
                    >
                      <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                    </IconButton>
                  </div>
                </CardFooter>
              )}
            </SectionCard>

            <FilterOptions
              isOpen={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
              onApplyFilter={handleApplyFilter}
              filterCategories={filterCategories}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
